"use client";

import { useCallback, useEffect, useState } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getLenisInstance } from "@/lib/lenis-instance";

type Theme = "dark" | "light";

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>("dark");

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => {
      const current = root.getAttribute("data-theme") as Theme | null;
      if (current) setThemeState(current);
    };
    sync();
    // Reacts to theme changes made by other components (e.g. the nav's toggle button calling its
    // own setTheme instance) — this hook's state is per-call, not shared, so without observing the
    // attribute directly, a consumer that never calls setTheme itself would never re-render.
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  const setTheme = useCallback((next: Theme) => {
    document.documentElement.setAttribute("data-theme", next);
    localStorage.setItem("exec9-theme", next);
    setThemeState(next);

    const refresh = () => {
      getLenisInstance()?.resize();
      ScrollTrigger.refresh();
    };
    window.setTimeout(refresh, 150);
    window.setTimeout(refresh, 600);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return { theme, setTheme, toggleTheme };
}
